/**
 * DragController - Device dragging management
 * 
 * Handles all device drag operations:
 * - Single device drag
 * - Multi-device drag (selection)
 * - Alt+drag to duplicate
 * - Long-press+drag to duplicate (touch)
 * - Undo/redo for movement
 */

import { getPointerPos, isLongPressTriggered, resetLongPressTriggered, isTouchMultiSelectMode } from './GestureController.js';
import { screenToCanvas } from './ViewportController.js';
import { setLastMousePosition } from './CableDragController.js';
import { getSelectedDevices } from './SelectionController.js';

// ============= STATE =============

// Device drag state
let dragState = null;

// Track if actual movement occurred during drag
let dragMoved = false;

// Z-index counter for bring-to-front
let maxZIndex = 10;

// Module dependencies (injected via init)
let instanceModel = null;
let selectDeviceFunc = null;
let duplicateDeviceFunc = null;
let renderAllCablesFunc = null;
let scheduleAutosaveFunc = null;
let recordActionFunc = null;

// ============= INIT =============

/**
 * Initialize the drag controller with dependencies
 * @param {Object} deps - Dependencies
 */
export function initDragController(deps) {
    instanceModel = deps.instanceModel;
    selectDeviceFunc = deps.selectDevice;
    duplicateDeviceFunc = deps.duplicateDevice;
    renderAllCablesFunc = deps.renderAllCables;
    scheduleAutosaveFunc = deps.scheduleAutosave;
    recordActionFunc = deps.recordAction;
}

// ============= STATE ACCESSORS =============

/**
 * Get current drag state
 */
export function getDragState() {
    return dragState;
}

/**
 * Check if currently dragging a device
 */
export function isDraggingDevice() {
    return dragState !== null;
}

/**
 * Check if device movement occurred during current drag
 */
export function hasDragMoved() {
    return dragMoved;
}

// ============= Z-INDEX MANAGEMENT =============

/**
 * Bring an element to front by increasing its z-index
 */
export function bringToFront(el) {
    maxZIndex++;
    el.style.zIndex = maxZIndex;
}

// ============= DEVICE DRAG FUNCTIONS =============

/**
 * Start dragging a device
 * @param {Event} e - Mouse or touch event
 * @param {Object} device - Device to drag
 */
export function startDeviceDrag(e, device) {
    if (e.type === 'mousedown' && e.button !== 0) return;
    e.preventDefault();
    
    const pos = getPointerPos(e);
    const canvasPos = screenToCanvas(pos.clientX, pos.clientY);
    
    const selectedDevices = getSelectedDevices();
    
    // Alt+drag (desktop) or long-press+drag (touch): duplicate the device and drag the copy
    let targetDevice = device;
    if (e.altKey || isLongPressTriggered()) {
        const duplicated = duplicateDeviceFunc(device.id, 0, 0);
        if (duplicated) {
            targetDevice = duplicated;
        }
    }
    
    // If dragging a device that's part of selection, keep the selection
    // but add this device to the selection if it wasn't already
    const isInSelection = selectedDevices.has(device.id);
    
    // Capture original positions for all devices that will move (for undo)
    const originalPositions = {};
    if (isInSelection && selectedDevices.size > 1) {
        // Multi-device drag: capture all selected device positions
        selectedDevices.forEach(id => {
            const d = instanceModel.getById(id);
            if (d) {
                originalPositions[id] = { x: d.position.x, y: d.position.y };
            }
        });
    } else {
        // Single device drag
        originalPositions[targetDevice.id] = { x: targetDevice.position.x, y: targetDevice.position.y };
    }
    
    dragState = {
        device: targetDevice,
        offsetX: canvasPos.x - targetDevice.position.x,
        offsetY: canvasPos.y - targetDevice.position.y,
        originalPositions: originalPositions, // Store for undo
        isMultiDrag: isInSelection && selectedDevices.size > 1
    };
    dragMoved = false; // Reset - will be set true on actual movement
    
    const el = document.getElementById(targetDevice.id);
    el.classList.add('dragging');
    
    // If the device is part of selection, just bring to front without clearing
    if (isInSelection) {
        bringToFront(el);
        // Bring all other selected devices to front too
        selectedDevices.forEach(id => {
            if (id !== targetDevice.id) {
                const otherEl = document.getElementById(id);
                if (otherEl) bringToFront(otherEl);
            }
        });
    } else if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !isTouchMultiSelectMode()) {
        // Only clear selection on normal click - modifiers let click handler deal with it
        // In touch multi-select mode, let the touchend handler deal with toggling
        selectDeviceFunc(targetDevice.id);
    }
}

/**
 * Handle device drag movement
 * @param {Event} e - Mouse or touch event
 */
export function handleDeviceDrag(e) {
    if (!dragState) return;
    
    const pos = getPointerPos(e);
    const canvasPos = screenToCanvas(pos.clientX, pos.clientY);
    
    // Track position for Alt key preview refresh (always, not just during drag)
    setLastMousePosition({ clientX: pos.clientX, clientY: pos.clientY });
    
    const selectedDevices = getSelectedDevices();
    
    const deltaX = (canvasPos.x - dragState.offsetX) - dragState.device.position.x;
    const deltaY = (canvasPos.y - dragState.offsetY) - dragState.device.position.y;
    
    // Mark that actual movement occurred (for touch multi-select detection)
    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        dragMoved = true;
    }
    
    dragState.device.position.x = canvasPos.x - dragState.offsetX;
    dragState.device.position.y = canvasPos.y - dragState.offsetY;
    
    const el = document.getElementById(dragState.device.id);
    el.style.left = dragState.device.position.x + 'px';
    el.style.top = dragState.device.position.y + 'px';
    
    // If this device is part of a selection, move all other selected devices too
    if (selectedDevices.has(dragState.device.id)) {
        selectedDevices.forEach(deviceId => {
            if (deviceId !== dragState.device.id) {
                const device = instanceModel.getById(deviceId);
                if (device) {
                    device.position.x += deltaX;
                    device.position.y += deltaY;
                    const deviceEl = document.getElementById(deviceId);
                    if (deviceEl) {
                        deviceEl.style.left = device.position.x + 'px';
                        deviceEl.style.top = device.position.y + 'px';
                    }
                }
            }
        });
    }
    
    renderAllCablesFunc();
}

/**
 * End device drag and record history
 * @param {Event} e - Mouse or touch event (optional)
 */
export function endDeviceDrag(e) {
    if (!dragState) return;
    
    const el = document.getElementById(dragState.device.id);
    el.classList.remove('dragging');
    
    // Only record history if actual movement occurred
    if (dragMoved && dragState.originalPositions) {
        const originalPositions = dragState.originalPositions;
        const deviceIds = Object.keys(originalPositions);
        
        // Capture final positions
        const finalPositions = {};
        deviceIds.forEach(id => {
            const d = instanceModel.getById(id);
            if (d) {
                finalPositions[id] = { x: d.position.x, y: d.position.y };
            }
        });
        
        const description = deviceIds.length > 1 
            ? `Move ${deviceIds.length} devices` 
            : 'Move device';
        
        recordActionFunc(
            'move-devices',
            description,
            // Undo: restore original positions
            () => {
                deviceIds.forEach(id => {
                    const device = instanceModel.getById(id);
                    if (device && originalPositions[id]) {
                        device.position.x = originalPositions[id].x;
                        device.position.y = originalPositions[id].y;
                        const deviceEl = document.getElementById(id);
                        if (deviceEl) {
                            deviceEl.style.left = device.position.x + 'px';
                            deviceEl.style.top = device.position.y + 'px';
                        }
                    }
                });
                renderAllCablesFunc();
                scheduleAutosaveFunc();
            },
            // Redo: restore final positions
            () => {
                deviceIds.forEach(id => {
                    const device = instanceModel.getById(id);
                    if (device && finalPositions[id]) {
                        device.position.x = finalPositions[id].x;
                        device.position.y = finalPositions[id].y;
                        const deviceEl = document.getElementById(id);
                        if (deviceEl) {
                            deviceEl.style.left = device.position.x + 'px';
                            deviceEl.style.top = device.position.y + 'px';
                        }
                    }
                });
                renderAllCablesFunc();
                scheduleAutosaveFunc();
            }
        );
    }
    
    scheduleAutosaveFunc(); // Save position change
    dragState = null;
    dragMoved = false;
    
    // Reset long-press triggered flag
    resetLongPressTriggered();
}
